/**
 * Cubic-bezier evaluator. Returns a `(t: number) => number` mapper that
 * matches CSS `cubic-bezier(x1, y1, x2, y2)` exactly.
 *
 * Use when a JS API (Lenis `scrollTo`, GSAP `CustomEase`, etc.) wants an
 * easing function instead of a CSS string, so the same curve drives both
 * CSS transitions and JS-driven animations without drift.
 *
 * Implementation: P0 = (0,0), P3 = (1,1). For input `t` (x-axis progress),
 * we Newton-Raphson-solve for the parameter `s` where x(s) = t, then return
 * y(s). Four iterations is enough for sub-pixel accuracy in animation contexts.
 */
export function cubicBezier(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
): (t: number) => number {
    // Bezier basis: x(s) = 3(1-s)²s·x1 + 3(1-s)s²·x2 + s³.
    // Expanded as a polynomial in s with cached coefficients:
    //   x(s) = ((ax·s + bx)·s + cx)·s        where cx = 3·x1, bx = 3·(x2-x1) - cx, ax = 1 - cx - bx
    const cx = 3 * x1;
    const bx = 3 * (x2 - x1) - cx;
    const ax = 1 - cx - bx;

    const cy = 3 * y1;
    const by = 3 * (y2 - y1) - cy;
    const ay = 1 - cy - by;

    const sampleX = (s: number) => ((ax * s + bx) * s + cx) * s;
    const sampleY = (s: number) => ((ay * s + by) * s + cy) * s;
    // Derivative dx/ds — needed for Newton iteration.
    const sampleDX = (s: number) => (3 * ax * s + 2 * bx) * s + cx;

    return (t: number) => {
        if (t <= 0) return 0;
        if (t >= 1) return 1;

        // Newton-Raphson: 4 iterations gets us well under a pixel for
        // typical animation contexts. Start at s = t (linear guess).
        let s = t;
        for (let i = 0; i < 4; i++) {
            const x = sampleX(s) - t;
            const dx = sampleDX(s);
            if (dx === 0) break;
            s -= x / dx;
        }
        return sampleY(s);
    };
}
